import { useMemo, useState } from "react";
import "./headerLanding.css";
import { useHomeContent } from "../../../pages/Landing/hooks/useHomeContent";
import { AuthModal } from "../modals/AuthModal/AuthModal";
import { FeedbackModal } from "../modals/FeedbackModal/FeedbackModal";
import { useLandingLanguages } from "../../../pages/Landing/hooks/useLandingLanguages";

const toDictionary = (value: unknown) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as Record<string, string>;
};

export const HeaderLanding = () => {
  const data = useHomeContent();
  const { availableCodes, currentCode, setLanguage } = useLandingLanguages();
  const languageLabels = useMemo(
    () => toDictionary(data?.header?.languageLabels),
    [data?.header?.languageLabels],
  );

  const [authOpen, setAuthOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  if (!data?.header) return null;
  const h = data.header;
  const feedbackModal = data.feedbackModal ?? {};

  const labels = {
    menuAria: h.menuAriaLabel ?? "menu",
    retailAria: h.retailAriaLabel ?? "retail",
    homeAria: h.homeAriaLabel ?? "home",
    loginAria: h.loginAriaLabel ?? "login",
    languageAria: h.languageAriaLabel ?? "language",
    closeAria: h.closeAriaLabel ?? "close",
    feedback: h.feedbackLabel ?? "",
    login: h.loginLabel ?? "",
    retailText: h.retailText ?? "",
  };

  const getLanguageLabel = (code: string, mobile = false) => {
    const normalized = code.toLowerCase();

    if (languageLabels[normalized]) return languageLabels[normalized];

    if (normalized === "ua") {
      return mobile
        ? h.mobileLangUkLabel ?? h.langUkLabel ?? normalized.toUpperCase()
        : h.langUkLabel ?? normalized.toUpperCase();
    }

    if (normalized === "en") {
      return mobile
        ? h.mobileLangEnLabel ?? h.langEnLabel ?? normalized.toUpperCase()
        : h.langEnLabel ?? normalized.toUpperCase();
    }

    return normalized.toUpperCase();
  };

  return (
    <>
      <header className="oc-header">
        <div className="oc-container">
          <div className="oc-header__row">
            <button
              className="oc-header__burger"
              onClick={() => setMobileMenu(true)}
              aria-label={labels.menuAria}
              type="button"
            >
              <span className="oc-ic oc-ic--menu" />
            </button>

            <div className="oc-header__left">
              <a
                className="oc-header__home"
                href={h.retailUrl}
                aria-label={labels.retailAria}
              >
                <span className="oc-ic oc-ic--home oc-ic--red" />
              </a>

              <div className="oc-header__contacts">
                <a className="oc-header__contact" href={`tel:${h.phoneHref}`}>
                  <span className="oc-ic oc-ic--phone oc-ic--red" />
                  <span>{h.phone}</span>
                </a>

                <a className="oc-header__contact" href={`mailto:${h.email}`}>
                  <span className="oc-ic oc-ic--email oc-ic--red" />
                  <span>{h.email}</span>
                </a>
              </div>
            </div>

            <a className="oc-header__brand" href="/" aria-label={labels.homeAria}>
              <img className="oc-header__logo" src={h.logo} alt={h.logoAlt ?? "logo"} />
            </a>

            <button
              className="oc-header__profile"
              onClick={() => setAuthOpen(true)}
              aria-label={labels.loginAria}
              type="button"
            >
              <span className="oc-ic oc-ic--profile" />
            </button>

            <div className="oc-header__right">
              <select
                className="oc-header__lang"
                value={currentCode}
                onChange={(e) => setLanguage(e.target.value)}
                aria-label={labels.languageAria}
              >
                {availableCodes.map((code) => (
                  <option key={code} value={code}>
                    {getLanguageLabel(code)}
                  </option>
                ))}
              </select>

              <button
                className="oc-header__btn oc-header__btn--gray"
                onClick={() => setFeedbackOpen(true)}
                type="button"
              >
                {labels.feedback}
              </button>

              <button
                className="oc-header__btn oc-header__btn--red"
                onClick={() => setAuthOpen(true)}
                type="button"
              >
                <span className="oc-ic oc-ic--profile oc-ic--white" />
                {labels.login}
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileMenu && (
        <div className="oc-mmenu" role="dialog" aria-modal="true">
          <div className="oc-mmenu__top">
            <button
              className="oc-mmenu__close"
              onClick={() => setMobileMenu(false)}
              aria-label={labels.closeAria}
              type="button"
            >
              <span className="oc-ic oc-ic--close" />
            </button>
          </div>

          <div className="oc-mmenu__panel">
            <a href={h.retailUrl} className="oc-mmenu__retail">
              <span className="oc-ic oc-ic--home" />
              <span>{labels.retailText}</span>
            </a>

            <div className="oc-mmenu__contacts">
              <a href={`tel:${h.phoneHref}`} className="oc-mmenu__contact">
                <span className="oc-ic oc-ic--phone" />
                <span>{h.phone}</span>
              </a>

              <a href={`mailto:${h.email}`} className="oc-mmenu__contact">
                <span className="oc-ic oc-ic--email" />
                <span>{h.email}</span>
              </a>
            </div>

            <div className="oc-mmenu__actions">
              <button
                className="oc-mmenu__btn oc-mmenu__btn--gray"
                onClick={() => {
                  setMobileMenu(false);
                  setFeedbackOpen(true);
                }}
                type="button"
              >
                {labels.feedback}
              </button>

              <button
                className="oc-mmenu__btn oc-mmenu__btn--red"
                onClick={() => {
                  setMobileMenu(false);
                  setAuthOpen(true);
                }}
                type="button"
              >
                <span className="oc-ic oc-ic--profile" />
                <span>{labels.login}</span>
              </button>
            </div>

            <div className="oc-mmenu__lang">
              <select
                value={currentCode}
                onChange={(e) => setLanguage(e.target.value)}
                aria-label={labels.languageAria}
              >
                {availableCodes.map((code) => (
                  <option key={`mobile-${code}`} value={code}>
                    {getLanguageLabel(code, true)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        initialMode="login"
        portalUrl={h.portalUrl ?? "/portal"}
        content={data.authModal}
      />

      <FeedbackModal
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        title={feedbackModal.title}
        thanksText={feedbackModal.thanksText}
        submitLabel={feedbackModal.submitLabel}
        formCopy={data.feedbackForm}
      />
    </>
  );
};
